// Shared date navigation header — sits ABOVE the Food log + Exercise cards.
// The selected date governs BOTH cards, so the control lives here (not nested
// inside DayLog). Prev/next-day buttons + a date label that toggles a month
// calendar popover; calendar days are color-coded by calorie status computed
// client-side from calorie_series + targets.daily_calories.

import { useState } from 'react'
import type { DashboardCaloriePoint } from '../lib/types'
import type { Strings } from '../lib/i18n'

// ── Calorie status thresholds ─────────────────────────────────────────────────
// Applied per-day relative to targets.daily_calories.
// If daily_calories is 0/null (no plan), any logged day is 'neutral'.
const CAL_UNDER_RATIO = 0.9    // < 90% of target  → under
const CAL_ON_MAX_RATIO = 1.05  // ≤ 105% of target → on-target; > 105% → over

type CalStatus = 'under' | 'on-target' | 'over' | 'neutral' | 'none'

function calStatus(calories: number | undefined, target: number): CalStatus {
  if (calories === undefined) return 'none'
  if (target <= 0) return 'neutral'   // no plan → can't grade
  const ratio = calories / target
  if (ratio < CAL_UNDER_RATIO) return 'under'
  if (ratio <= CAL_ON_MAX_RATIO) return 'on-target'
  return 'over'
}

// Status → CSS class suffix for the day cell fill.
const STATUS_CLASS: Record<CalStatus, string> = {
  'none':       '',
  'neutral':    'cal-neutral',
  'under':      'cal-under',
  'on-target':  'cal-on',
  'over':       'cal-over',
}

// ── Calendar helpers ──────────────────────────────────────────────────────────

/** Return the YYYY-MM-DD strings for every cell in a Mon-first 6-week calendar grid for the given year/month. */
function calendarGrid(year: number, month: number): string[] {
  // First day of the month.
  const first = new Date(year, month - 1, 1)
  // ISO day-of-week: Mon=1 … Sun=7; we want Mon at col 0.
  const startDow = ((first.getDay() + 6) % 7) // 0=Mon … 6=Sun
  // Last day of the month.
  const daysInMonth = new Date(year, month, 0).getDate()

  const cells: string[] = []
  // Leading days from previous month.
  const prevMonthLast = new Date(year, month - 1, 0).getDate()
  for (let i = startDow - 1; i >= 0; i--) {
    const d = prevMonthLast - i
    const prevMonth = month === 1 ? 12 : month - 1
    const prevYear = month === 1 ? year - 1 : year
    cells.push(`${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`)
  }
  // Days in this month.
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`)
  }
  // Trailing days to fill 6 rows × 7 cols = 42 cells.
  const nextMonth = month === 12 ? 1 : month + 1
  const nextYear = month === 12 ? year + 1 : year
  let nd = 1
  while (cells.length < 42) {
    cells.push(`${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(nd).padStart(2, '0')}`)
    nd++
  }
  return cells
}

function parseYM(date: string): [number, number] {
  const [y, m] = date.split('-').map(Number)
  return [y, m]
}

function shiftMonth(year: number, month: number, delta: number): [number, number] {
  let m = month + delta
  let y = year
  while (m > 12) { m -= 12; y++ }
  while (m < 1)  { m += 12; y-- }
  return [y, m]
}

/** Increment/decrement a YYYY-MM-DD string by `delta` days (UTC-safe). */
function shiftDate(date: string, delta: number): string {
  const d = new Date(date)
  d.setUTCDate(d.getUTCDate() + delta)
  return d.toISOString().slice(0, 10)
}

// ── Calendar component ────────────────────────────────────────────────────────

function CalendarPicker({
  selectedDate,
  calByDate,
  dailyCalTarget,
  s,
  onSelect,
  onClose,
}: {
  selectedDate: string
  calByDate: Record<string, number>
  dailyCalTarget: number
  s: Strings
  onSelect: (date: string) => void
  onClose: () => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [[year, month], setYM] = useState<[number, number]>(() => parseYM(selectedDate))

  const todayYM = parseYM(today)
  const atCurrentMonth = year === todayYM[0] && month === todayYM[1]

  const cells = calendarGrid(year, month)
  const currentMonthStr = `${year}-${String(month).padStart(2, '0')}`

  return (
    <div className="cal-popover" role="dialog" aria-label={s.fmtMonthTitle(year, month)}>
      {/* Month navigation header */}
      <div className="cal-header">
        <button
          type="button"
          className="cal-nav-btn"
          onClick={() => setYM(shiftMonth(year, month, -1))}
          aria-label="prev month"
        >
          ‹
        </button>
        <span className="cal-month-title">{s.fmtMonthTitle(year, month)}</span>
        <button
          type="button"
          className="cal-nav-btn"
          onClick={() => !atCurrentMonth && setYM(shiftMonth(year, month, 1))}
          disabled={atCurrentMonth}
          aria-label="next month"
        >
          ›
        </button>
      </div>

      {/* Weekday headers: Mon–Sun */}
      <div className="cal-grid">
        {s.calWeekdays.map((wd) => (
          <div key={wd} className="cal-weekday">{wd}</div>
        ))}

        {/* Day cells */}
        {cells.map((date) => {
          const inMonth = date.startsWith(currentMonthStr)
          const isToday = date === today
          const isSelected = date === selectedDate
          const status = inMonth ? calStatus(calByDate[date], dailyCalTarget) : 'none'
          const statusCls = STATUS_CLASS[status]

          const cls = [
            'cal-day',
            inMonth ? '' : 'cal-day-other',
            statusCls ? `cal-day-${statusCls}` : '',
            isToday ? 'cal-day-today' : '',
            isSelected ? 'cal-day-selected' : '',
          ].filter(Boolean).join(' ')

          return (
            <button
              key={date}
              type="button"
              className={cls}
              onClick={() => {
                if (inMonth) {
                  onSelect(date)
                  onClose()
                }
              }}
              tabIndex={inMonth ? 0 : -1}
            >
              {parseInt(date.slice(8))}
            </button>
          )
        })}
      </div>

      {/* Legend */}
      <div className="cal-legend">
        <span className="cal-legend-item">
          <span className="cal-legend-dot cal-legend-dot-under" />
          {s.calLegendUnder}
        </span>
        <span className="cal-legend-item">
          <span className="cal-legend-dot cal-legend-dot-on" />
          {s.calLegendOnTarget}
        </span>
        <span className="cal-legend-item">
          <span className="cal-legend-dot cal-legend-dot-over" />
          {s.calLegendOver}
        </span>
      </div>
    </div>
  )
}

// ── Main DateNav component ────────────────────────────────────────────────────

interface Props {
  /** Currently selected date (YYYY-MM-DD) — from the loaded dashboard's day.date. */
  date: string
  /** Full calorie series — drives per-day calendar coloring. */
  calorieSeries: DashboardCaloriePoint[]
  /** Daily calorie target for calendar status grading (0 = no plan). */
  dailyCalTarget: number
  strings: Strings
  onDateChange: (date: string) => void
}

export function DateNav({ date, calorieSeries, dailyCalTarget, strings: s, onDateChange }: Props) {
  const [calOpen, setCalOpen] = useState(false)
  const today = new Date().toISOString().slice(0, 10)
  const isToday = date === today

  // Build a date→calories lookup from the full series for calendar coloring.
  const calByDate: Record<string, number> = {}
  for (const p of calorieSeries) {
    calByDate[p.date] = p.calories
  }

  return (
    <div className="dash-date-nav">
      <div className="date-nav-row">
        <button
          type="button"
          className="date-nav-btn"
          onClick={() => { setCalOpen(false); onDateChange(shiftDate(date, -1)) }}
          aria-label="prev"
        >
          ‹
        </button>
        <button
          type="button"
          className="date-nav-label"
          onClick={() => setCalOpen((o) => !o)}
          aria-expanded={calOpen}
          aria-haspopup="dialog"
        >
          {isToday ? s.dayToday : s.fmtDate(date)}
          <span className="date-nav-cal-icon" aria-hidden="true">▾</span>
        </button>
        <button
          type="button"
          className="date-nav-btn"
          onClick={() => { setCalOpen(false); onDateChange(shiftDate(date, 1)) }}
          disabled={isToday}
          aria-label="next"
        >
          ›
        </button>
      </div>

      {calOpen && (
        <CalendarPicker
          selectedDate={date}
          calByDate={calByDate}
          dailyCalTarget={dailyCalTarget}
          s={s}
          onSelect={(d) => onDateChange(d)}
          onClose={() => setCalOpen(false)}
        />
      )}
    </div>
  )
}
